// src/components/stats/StatsKit.jsx
// Petits composants de statistiques partagés par la page client et la page admin.
// Graphiques en SVG pur : aucune dépendance à installer.
import { useRef, useState } from 'react'
import { ArrowUpRight, ArrowDownRight, Minus, Download, Loader2 } from 'lucide-react'
import Card from '../ui/Card'

export const PALETTE = ['#0D9488', '#0F1B3D', '#D4A017', '#7C3AED', '#EF4444', '#0EA5E9']

/* ---------- Formatage ---------- */
export const fmtNum = (n) => Number(n || 0).toLocaleString('fr-FR')

export const fmtMoney = (n, currency = 'MAD') =>
  `${Number(n || 0).toLocaleString('fr-FR', { maximumFractionDigits: 0 })} ${currency}`

export const fmtPct = (n) => `${Number(n || 0).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} %`

const fmtDay = (iso) => {
  if (!iso) return ''
  const d = new Date(`${iso}T00:00:00`)
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })
}

export const fmtDateTime = (d) =>
  d ? new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'

/* ---------- Export CSV (généré dans le navigateur à partir des séries) ---------- */
export function downloadSeriesCsv(filename, seriesList) {
  // seriesList : [{ label, data: [{ date, value }] }]
  if (!seriesList.length || !seriesList[0].data?.length) return
  const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`
  const header = ['Date', ...seriesList.map((s) => s.label)]
  const rows = seriesList[0].data.map((point, i) => [
    point.date,
    ...seriesList.map((s) => s.data[i]?.value ?? 0),
  ])
  const csv = '﻿' + [header, ...rows].map((r) => r.map(esc).join(';')).join('\r\n')
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }))
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

export function ExportButton({ onClick, disabled }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-700 hover:bg-graysoft disabled:opacity-50 disabled:cursor-not-allowed"
    >
      <Download className="w-4 h-4" />
      Exporter (.CSV)
    </button>
  )
}

/* ---------- Sélecteur de période ---------- */
const RANGES = [
  { days: 7, label: '7 jours' },
  { days: 30, label: '30 jours' },
  { days: 90, label: '90 jours' },
]

export function RangeTabs({ value, onChange }) {
  return (
    <div className="inline-flex rounded-xl bg-graysoft p-1">
      {RANGES.map((r) => (
        <button
          key={r.days}
          onClick={() => onChange(r.days)}
          className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
            value === r.days ? 'bg-white text-navy font-semibold shadow-sm' : 'text-gray-500 hover:text-navy'
          }`}
        >
          {r.label}
        </button>
      ))}
    </div>
  )
}

/* ---------- Carte KPI avec variation ---------- */
export function KpiCard({ label, value, change, icon: Icon, hint, tone = 'teal' }) {
  const tones = {
    teal: 'bg-teal-50 text-teal',
    navy: 'bg-navy-50 text-navy',
    gold: 'bg-gold-50 text-gold',
    red: 'bg-red-50 text-red-500',
  }

  let badge = null
  if (change === null || change === undefined) {
    badge = <span className="text-[11px] text-gray-400">Nouveau</span>
  } else if (change > 0) {
    badge = (
      <span className="inline-flex items-center gap-0.5 text-[11px] font-semibold text-teal-700">
        <ArrowUpRight className="w-3 h-3" />
        {fmtPct(change)}
      </span>
    )
  } else if (change < 0) {
    badge = (
      <span className="inline-flex items-center gap-0.5 text-[11px] font-semibold text-red-500">
        <ArrowDownRight className="w-3 h-3" />
        {fmtPct(Math.abs(change))}
      </span>
    )
  } else {
    badge = (
      <span className="inline-flex items-center gap-0.5 text-[11px] text-gray-400">
        <Minus className="w-3 h-3" />
        Stable
      </span>
    )
  }

  return (
    <Card className="p-5" hover>
      <div className="flex items-start justify-between mb-4">
        {Icon && (
          <div className={`p-2.5 rounded-xl ${tones[tone] || tones.teal}`}>
            <Icon className="w-5 h-5" />
          </div>
        )}
        {badge}
      </div>
      <p className="text-xs text-gray-500 mb-1">{label}</p>
      <p className="text-2xl font-bold text-navy">{value}</p>
      {hint && <p className="text-[11px] text-gray-400 mt-1">{hint}</p>}
    </Card>
  )
}

/* ---------- Bloc avec titre ---------- */
export function Section({ title, subtitle, right, children, className = '' }) {
  return (
    <Card className={`p-5 ${className}`}>
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div>
          <h2 className="font-bold text-navy">{title}</h2>
          {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
        </div>
        {right}
      </div>
      {children}
    </Card>
  )
}

export function EmptyHint({ children = 'Aucune donnée sur cette période.' }) {
  return <p className="text-sm text-gray-400 py-8 text-center">{children}</p>
}

export function PageLoader() {
  return (
    <Card className="p-16 flex items-center justify-center">
      <Loader2 className="w-6 h-6 text-teal animate-spin" />
    </Card>
  )
}

/* ---------- Graphique en lignes (multi-séries, infobulle au survol) ---------- */
const W = 720
const H = 240
const PAD = { top: 12, right: 12, bottom: 28, left: 40 }

const niceMax = (max) => {
  if (max <= 4) return 4
  const pow = Math.pow(10, Math.floor(Math.log10(max)))
  const n = max / pow
  const step = n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10
  return step * pow
}

export function TrendChart({ series, formatValue = fmtNum }) {
  // series : [{ key, label, color, data: [{ date, value }] }]
  const ref = useRef(null)
  const [hover, setHover] = useState(null)

  const first = series[0]?.data || []
  const n = first.length
  if (n === 0) return <EmptyHint />

  const rawMax = Math.max(1, ...series.flatMap((s) => s.data.map((p) => p.value)))
  const max = niceMax(rawMax)
  const innerW = W - PAD.left - PAD.right
  const innerH = H - PAD.top - PAD.bottom

  const x = (i) => PAD.left + (n === 1 ? innerW / 2 : (i / (n - 1)) * innerW)
  const y = (v) => PAD.top + innerH - (v / max) * innerH

  const ticks = [0, 0.25, 0.5, 0.75, 1].map((t) => t * max)
  const labelIdx = n <= 7 ? first.map((_, i) => i) : [0, Math.floor((n - 1) / 3), Math.floor(((n - 1) * 2) / 3), n - 1]

  const onMove = (e) => {
    const rect = ref.current?.getBoundingClientRect()
    if (!rect) return
    const px = ((e.clientX - rect.left) / rect.width) * W
    const ratio = (px - PAD.left) / innerW
    const idx = Math.max(0, Math.min(n - 1, Math.round(ratio * (n - 1))))
    setHover(idx)
  }

  const hoverX = hover !== null ? x(hover) : null

  return (
    <div className="relative">
      <svg
        ref={ref}
        viewBox={`0 0 ${W} ${H}`}
        className="w-full h-auto"
        onMouseMove={onMove}
        onMouseLeave={() => setHover(null)}
        role="img"
        aria-label="Évolution sur la période"
      >
        {ticks.map((t, i) => (
          <g key={i}>
            <line x1={PAD.left} x2={W - PAD.right} y1={y(t)} y2={y(t)} stroke="#E5E7EB" strokeWidth="1" strokeDasharray={i === 0 ? '0' : '3 4'} />
            <text x={PAD.left - 8} y={y(t) + 4} textAnchor="end" fontSize="11" fill="#9CA3AF">
              {Number.isInteger(t) ? fmtNum(t) : t.toFixed(1)}
            </text>
          </g>
        ))}

        {labelIdx.map((i) => (
          <text key={i} x={x(i)} y={H - 8} textAnchor={i === 0 ? 'start' : i === n - 1 ? 'end' : 'middle'} fontSize="11" fill="#9CA3AF">
            {fmtDay(first[i].date)}
          </text>
        ))}

        {series.map((s, si) => {
          const path = s.data.map((p, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(p.value).toFixed(1)}`).join(' ')
          const area = `${path} L${x(n - 1).toFixed(1)},${y(0)} L${x(0).toFixed(1)},${y(0)} Z`
          return (
            <g key={s.key}>
              {si === 0 && <path d={area} fill={s.color} opacity="0.08" />}
              <path d={path} fill="none" stroke={s.color} strokeWidth="2.25" strokeLinejoin="round" strokeLinecap="round" />
              {hover !== null && <circle cx={x(hover)} cy={y(s.data[hover].value)} r="4" fill="#fff" stroke={s.color} strokeWidth="2" />}
            </g>
          )
        })}

        {hoverX !== null && <line x1={hoverX} x2={hoverX} y1={PAD.top} y2={H - PAD.bottom} stroke="#CBD5E1" strokeWidth="1" />}
      </svg>

      {hover !== null && (
        <div
          className="absolute top-2 pointer-events-none bg-white border border-gray-100 shadow-dropdown rounded-lg px-3 py-2 text-xs"
          style={{ left: `${Math.min(Math.max((hoverX / W) * 100, 14), 86)}%`, transform: 'translateX(-50%)' }}
        >
          <p className="font-semibold text-navy mb-1">{fmtDay(first[hover].date)}</p>
          {series.map((s) => (
            <p key={s.key} className="flex items-center gap-2 text-gray-600">
              <span className="w-2 h-2 rounded-full" style={{ background: s.color }} />
              {s.label} : <span className="font-semibold text-navy">{formatValue(s.data[hover].value)}</span>
            </p>
          ))}
        </div>
      )}
    </div>
  )
}

/* ---------- Légende cliquable (active/désactive une série) ---------- */
export function SeriesToggles({ items, active, onToggle }) {
  return (
    <div className="flex flex-wrap gap-2">
      {items.map((it) => {
        const on = active.includes(it.key)
        return (
          <button
            key={it.key}
            onClick={() => onToggle(it.key)}
            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs border transition-colors ${
              on ? 'border-gray-300 bg-white text-navy font-semibold' : 'border-gray-100 bg-graysoft text-gray-400'
            }`}
          >
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: on ? it.color : '#CBD5E1' }} />
            {it.label}
          </button>
        )
      })}
    </div>
  )
}

/* ---------- Liste à barres horizontales ---------- */
export function BarList({ items, formatValue = fmtNum, color = PALETTE[0], empty }) {
  // items : [{ label, value, sub? }]
  if (!items || items.length === 0) return <EmptyHint>{empty}</EmptyHint>
  const max = Math.max(1, ...items.map((i) => i.value))
  return (
    <ul className="space-y-3">
      {items.map((it, i) => (
        <li key={`${it.label}-${i}`}>
          <div className="flex items-center justify-between gap-3 text-sm mb-1">
            <span className="text-navy truncate">
              {it.label}
              {it.sub && <span className="text-gray-400 text-xs ml-2">{it.sub}</span>}
            </span>
            <span className="font-semibold text-navy tabular-nums">{formatValue(it.value)}</span>
          </div>
          <div className="h-2 rounded-full bg-graysoft overflow-hidden">
            <div className="h-full rounded-full" style={{ width: `${Math.max(3, (it.value / max) * 100)}%`, background: color }} />
          </div>
        </li>
      ))}
    </ul>
  )
}

/* ---------- Anneau de répartition ---------- */
export function DonutChart({ items, empty }) {
  // items : [{ label, value }]
  const total = (items || []).reduce((s, i) => s + i.value, 0)
  if (!items || items.length === 0 || total === 0) return <EmptyHint>{empty}</EmptyHint>

  const R = 52
  const C = 2 * Math.PI * R
  let offset = 0

  return (
    <div className="flex items-center gap-5 flex-wrap">
      <svg viewBox="0 0 140 140" className="w-32 h-32 shrink-0" role="img" aria-label="Répartition">
        <circle cx="70" cy="70" r={R} fill="none" stroke="#F1F5F9" strokeWidth="18" />
        {items.map((it, i) => {
          const len = (it.value / total) * C
          const el = (
            <circle
              key={it.label}
              cx="70"
              cy="70"
              r={R}
              fill="none"
              stroke={PALETTE[i % PALETTE.length]}
              strokeWidth="18"
              strokeDasharray={`${len} ${C - len}`}
              strokeDashoffset={-offset}
              transform="rotate(-90 70 70)"
            />
          )
          offset += len
          return el
        })}
        <text x="70" y="68" textAnchor="middle" fontSize="18" fontWeight="700" fill="#0F1B3D">{fmtNum(total)}</text>
        <text x="70" y="84" textAnchor="middle" fontSize="10" fill="#9CA3AF">total</text>
      </svg>
      <ul className="space-y-1.5 text-sm min-w-[8rem] flex-1">
        {items.map((it, i) => (
          <li key={it.label} className="flex items-center justify-between gap-3">
            <span className="flex items-center gap-2 text-gray-600 truncate">
              <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: PALETTE[i % PALETTE.length] }} />
              {it.label}
            </span>
            <span className="font-semibold text-navy tabular-nums">{Math.round((it.value / total) * 100)} %</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

/* ---------- Message d'avertissement du backend (colonne ou table absente) ---------- */
export function Warnings({ items }) {
  if (!items || items.length === 0) return null
  return (
    <div className="p-3 bg-gold-50 border border-gold/30 rounded-xl text-xs text-gray-600">
      <p className="font-semibold text-navy mb-1">Certaines données n'ont pas pu être calculées :</p>
      <ul className="list-disc pl-4 space-y-0.5">
        {items.map((w, i) => <li key={i}>{w}</li>)}
      </ul>
    </div>
  )
}
